/**
 * PermissionsContext — viewer-scoped field-policy SSOT.
 *
 * Holds the result of GET /api/me/permissions/ (BE 46116f1c, WS-ENT-3).
 * Mount inside Layout above FocusProvider so every authenticated surface
 * can read via `useFieldPolicy()` / `<Gated permission="x">` without
 * each surface refetching independently.
 *
 * Stale-while-revalidate posture:
 *   - The first fetch fires on mount.
 *   - `cache_max_age` from the BE response (5 min today) drives a
 *     deterministic timer-based refetch. We honour the server's hint
 *     instead of hard-coding because the BE owns the cache window.
 *   - During the stale window the cached `permissions` Set is the
 *     answer to every `hasPermission` query — no second fetch.
 *   - A fetch failure leaves the previous Set in place (matches
 *     UserProvider's "don't clear a good cached copy" posture).
 *
 * Why a Set instead of an array on the context value:
 *   - `hasPermission` is the hot-path call from render-time gates.
 *     `Array.includes` is O(n); `Set.has` is O(1). With 50+ enforce
 *     sites in Phase 2 (and a permissions catalogue that's expected
 *     to grow), O(n) per gate becomes measurable jank.
 *   - The Set is rebuilt only when the wire payload changes; consumers
 *     can keep a stable reference between fetches.
 *
 * captureError SSOT for fetch failures.
 *
 * exactOptionalPropertyTypes — `error` is `Error | null`, never
 * `undefined`. Same posture as the wire-error fields elsewhere.
 */

import {
    createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
    type ReactNode,
} from 'react';
import { getMePermissions } from '../../services/permissionsService';
import { captureError } from '../errors/captureError';

type PermissionsContextValue = {
    /** Set of permission keys granted to the viewer. Empty until the
     *  first fetch resolves; consumers can treat empty as "no permissions"
     *  (not "still loading" — `loading` covers that). */
    permissions: ReadonlySet<string>;
    /** Canonical viewer role label from the wire payload. Empty string
     *  until the first fetch resolves. */
    role: string;
    /** True only while the FIRST fetch is in flight. Subsequent
     *  background revalidations don't flip this back — stale-while-
     *  revalidate keeps the cached Set visible. */
    loading: boolean;
    /** Last fetch error, if any. `null` after a successful refetch. */
    error: Error | null;
    /** `true` iff `permissions.has(permission)`. Identical to consuming
     *  the Set directly; surfaced as a method so the contract is stable
     *  even if we move the internal storage off Set later. */
    hasPermission: (permission: string) => boolean;
    /** Imperative refetch — used by tests + by surfaces that just
     *  performed a role-changing action (grant, approve, etc.) and
     *  want their gates to update before the next stale-window timer. */
    refetch: () => Promise<void>;
};

const PermissionsContext = createContext<PermissionsContextValue | null>(null);

/** Empty Set reused so consumers that read `permissions` before the
 *  first fetch resolves get a stable reference (no spurious re-renders
 *  on identity equality). */
const EMPTY_SET: ReadonlySet<string> = new Set<string>();

/** Fallback cache window if the BE omits `cache_max_age` (defensive —
 *  the schema requires it, but a future BE change could relax it).
 *  Matches the WS-ENT-3 brief's "5-min stale" guidance. */
const DEFAULT_CACHE_MAX_AGE_SECONDS = 300;

type Props = {
    children: ReactNode;
    /** Opt-out of the initial fetch — used by tests that seed the
     *  context manually. */
    skipInitialFetch?: boolean;
    /** Seed the context with a known permission set + role. Tests use
     *  this to assert gate behaviour without going through the wire
     *  layer; production code should never pass this. */
    seed?: { permissions: ReadonlyArray<string>; role: string };
};

export function PermissionsProvider({
    children,
    skipInitialFetch = false,
    seed,
}: Props) {
    const [permissions, setPermissions] = useState<ReadonlySet<string>>(
        seed ? new Set(seed.permissions) : EMPTY_SET,
    );
    const [role, setRole] = useState<string>(seed?.role ?? '');
    const [loading, setLoading] = useState<boolean>(!skipInitialFetch && !seed);
    const [error, setError] = useState<Error | null>(null);
    const [cacheMaxAgeSeconds, setCacheMaxAgeSeconds] = useState<number>(
        DEFAULT_CACHE_MAX_AGE_SECONDS,
    );
    // De-dupe concurrent fetches — if a stale-window timer + an
    // imperative refetch fire on the same tick, piggyback.
    const inflightRef = useRef<Promise<void> | null>(null);

    const refetch = useCallback(async () => {
        if (inflightRef.current) return inflightRef.current;
        const p = (async () => {
            try {
                const payload = await getMePermissions();
                setPermissions(new Set(payload.permissions));
                setRole(payload.role);
                // Honour the BE's cache hint; clamp to a sane minimum
                // so a server bug returning 0 doesn't busy-loop the
                // refetch timer.
                setCacheMaxAgeSeconds(
                    Math.max(payload.cache_max_age, 1),
                );
                setError(null);
            } catch (err) {
                // Don't clear the cached Set — a transient failure
                // shouldn't strip the viewer's affordances. SSOT log.
                captureError(err, {
                    scope: 'PermissionsProvider',
                    op: 'refetch',
                });
                setError(err instanceof Error ? err : new Error(String(err)));
            } finally {
                setLoading(false);
                inflightRef.current = null;
            }
        })();
        inflightRef.current = p;
        return p;
    }, []);

    // Initial fetch — gated by `skipInitialFetch` and `seed` so tests
    // can pin the context without a wire round-trip.
    useEffect(() => {
        if (skipInitialFetch || seed) return;
        void refetch();
    }, [skipInitialFetch, seed, refetch]);

    // Stale-window timer. Fires a background refetch every
    // `cacheMaxAgeSeconds` to keep the cached Set fresh. The check is
    // cheap (one fetch every 5 min by default) and the BE owns the
    // cadence — we never hard-code a window of our own.
    useEffect(() => {
        if (skipInitialFetch || seed) return;
        const intervalMs = cacheMaxAgeSeconds * 1000;
        const id = setInterval(() => { void refetch(); }, intervalMs);
        return () => clearInterval(id);
    }, [skipInitialFetch, seed, refetch, cacheMaxAgeSeconds]);

    const hasPermission = useCallback(
        (permission: string): boolean => permissions.has(permission),
        [permissions],
    );

    const value = useMemo<PermissionsContextValue>(
        () => ({ permissions, role, loading, error, hasPermission, refetch }),
        [permissions, role, loading, error, hasPermission, refetch],
    );

    return (
        <PermissionsContext.Provider value={value}>
            {children}
        </PermissionsContext.Provider>
    );
}

/** Read the raw permissions context. Surfaces use `useFieldPolicy` for
 *  the gate-friendly slim contract; tests + advanced consumers can use
 *  this when they need the full value (loading, error, refetch). */
export function usePermissionsContext(): PermissionsContextValue {
    const ctx = useContext(PermissionsContext);
    if (!ctx) {
        throw new Error(
            'usePermissionsContext must be used inside <PermissionsProvider>',
        );
    }
    return ctx;
}
